/**
 * Session state.
 *
 * Statuses:
 *  - `booting`         — deciding (refresh + /me, or cached session);
 *  - `active`          — signed in, token in memory, /me fresh from the server;
 *  - `offline`         — server unreachable; running on the cached /me so field
 *                        users keep working. Becomes `active` on reconnect;
 *  - `reauth_required` — the server refused the refresh. The cached session and
 *                        the outbox are kept; the UI shows "Sign in to sync".
 *                        Queued mutations are never discarded for this;
 *  - `signed_out`      — no session and nothing cached.
 *
 * This module holds no authority: permissions come from the server and are
 * only cached so the UI can render without a round trip.
 */
import { useSyncExternalStore } from 'react';

import { exchangeCode, fetchAuthConfig, fetchMe, logoutRequest } from '@/lib/api/auth';
import {
  api,
  hasUsableAccessToken,
  refreshAccessToken,
  setAccessToken,
  setSessionHooks,
} from '@/lib/api/client';
import { isApiError } from '@/lib/api/errors';
import type { AuthTokenResponse, Me } from '@/lib/api/types';
import { clearProtectedData, switchToMemoryDatabase, type MemoryBackend } from '@/lib/db/db';
import { upsertFromMe } from '@/lib/db/projects';
import { getCachedSession, saveCachedSession } from '@/lib/db/session';
import { reconcileLanguage } from '@/lib/language-preference';

import {
  codeChallengeFor,
  consumePendingSignIn,
  randomToken,
  safeReturnPath,
  savePendingSignIn,
  type AuthPurpose,
  type PendingSignIn,
} from './pkce';
import { redirectTo } from './redirect';
import { markStepUpSignedIn } from './step-up-store';
import {
  beginViewAs,
  endViewAsLocally,
  getViewAs,
  isViewAs,
  subscribeToViewAs,
  type ViewAsInfo,
} from './view-as';

export type SessionStatus = 'booting' | 'active' | 'offline' | 'reauth_required' | 'signed_out';

export type SessionSnapshot = {
  status: SessionStatus;
  me: Me | null;
};

let snapshot: SessionSnapshot = { status: 'booting', me: null };
const listeners = new Set<() => void>();
let bootPromise: Promise<SessionSnapshot> | null = null;

function set(next: SessionSnapshot): void {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

export function getSession(): SessionSnapshot {
  return snapshot;
}

export function subscribeToSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSession(): SessionSnapshot {
  return useSyncExternalStore(subscribeToSession, getSession, getSession);
}

setSessionHooks({
  onSessionLost: () => {
    set(
      snapshot.me
        ? { status: 'reauth_required', me: snapshot.me }
        : { status: 'signed_out', me: null },
    );
  },
  onTokenRefreshed: () => undefined,
});

/** Persist a fresh /me, wiping local data first if a different person signed in. */
async function adoptMe(me: Me): Promise<void> {
  const cached = await getCachedSession().catch(() => null);
  if (cached && cached.user.id !== me.user.id) await clearProtectedData();
  await saveCachedSession(me);
  await upsertFromMe(me.projects);
  set({ status: 'active', me });
  // Viewing as someone (staff): their language never changes this device's.
  if (isViewAs()) return;
  // The account's language (or a choice still to send): never holds up the session.
  void reconcileLanguage(me).catch(() => undefined);
}

export async function reloadMe(): Promise<Me> {
  const me = await fetchMe();
  await adoptMe(me);
  return me;
}

async function fallBackToCache(cached: Me | null, status: 'offline' | 'reauth_required') {
  set(cached ? { status, me: cached } : { status: 'signed_out', me: null });
}

async function boot(): Promise<SessionSnapshot> {
  const cached = await getCachedSession().catch(() => null);
  const outcome = await refreshAccessToken();
  if (outcome === 'refused') {
    await fallBackToCache(cached, 'reauth_required');
  } else if (outcome === 'unreachable') {
    await fallBackToCache(cached, 'offline');
  } else {
    try {
      await reloadMe();
    } catch (error) {
      if (isApiError(error) && error.needsAuth) await fallBackToCache(cached, 'reauth_required');
      else await fallBackToCache(cached, 'offline');
    }
  }
  return snapshot;
}

/** Decide the session once per page load; later callers share the answer. */
export function bootSession(): Promise<SessionSnapshot> {
  bootPromise ??= boot();
  return bootPromise;
}

/**
 * Make sure there is a usable token before talking to the server (used by the
 * sync engine on reconnect: refresh first, then sync).
 */
export async function ensureOnline(): Promise<'ok' | 'offline' | 'auth_required'> {
  if (snapshot.status === 'signed_out' || snapshot.status === 'booting') return 'auth_required';
  if (snapshot.status === 'reauth_required') return 'auth_required';

  if (!hasUsableAccessToken()) {
    const outcome = await refreshAccessToken();
    if (outcome === 'unreachable') {
      if (snapshot.status === 'active') set({ ...snapshot, status: 'offline' });
      return 'offline';
    }
    if (outcome === 'refused') return 'auth_required';
  }

  if (snapshot.status === 'offline') {
    try {
      await reloadMe();
    } catch (error) {
      if (isApiError(error) && error.needsAuth) return 'auth_required';
      return 'offline';
    }
  }
  return 'ok';
}

/** Mark the session offline after a network failure elsewhere (keeps the cache). */
export function noteUnreachable(): void {
  if (snapshot.status === 'active') set({ ...snapshot, status: 'offline' });
}

// ---------------------------------------------------------------------------
// Sign-in (PKCE redirect to KorOS identity) and callback
// ---------------------------------------------------------------------------

/**
 * @param extraParams extra authorize parameters, e.g. `prompt=login` and
 *   `max_age=0` for a step-up (the person must sign in again, now).
 * @param options.nonce a nonce chosen by the server (the handover manifest
 *   hash) instead of a random one.
 * @param options.state a state chosen by the server (the Django admin sign-in
 *   binds it to the browser) instead of a random one.
 * @param options.purpose what the callback does with the code (default: sign in).
 */
export async function startSignIn(
  returnTo: string,
  extraParams: Readonly<Record<string, string>> = {},
  options: { nonce?: string; state?: string; purpose?: AuthPurpose } = {},
): Promise<void> {
  const config = await fetchAuthConfig();
  const verifier = randomToken(48);
  const state = options.state ?? randomToken(24);
  const nonce = options.nonce ?? randomToken(24);
  const challenge = await codeChallengeFor(verifier);

  savePendingSignIn({
    state,
    nonce,
    code_verifier: verifier,
    redirect_uri: config.redirect_uri,
    return_to: safeReturnPath(returnTo),
    created_at: Date.now(),
    ...(options.purpose ? { purpose: options.purpose } : {}),
  });

  const url = new URL(config.authorize_url);
  url.searchParams.set('response_type', config.response_type);
  url.searchParams.set('client_id', config.client_id);
  url.searchParams.set('redirect_uri', config.redirect_uri);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', config.code_challenge_method);
  url.searchParams.set('state', state);
  url.searchParams.set('nonce', nonce);
  if (config.scopes.length) url.searchParams.set('scope', config.scopes.join(' '));
  for (const [key, value] of Object.entries(extraParams)) url.searchParams.set(key, value);
  redirectTo(url.toString());
}

export class SignInError extends Error {
  constructor(
    readonly reason:
      | 'state_mismatch'
      | 'missing_code'
      | 'provider_error'
      | 'exchange_failed'
      | 'provider_unavailable'
      | 'account_erased'
      | 'offline',
    message: string,
  ) {
    super(message);
    this.name = 'SignInError';
  }
}

/**
 * Finish the redirect: verify state, exchange the code, load /me. Returns where
 * to go. `pending` is passed when the callback already consumed it (to look at
 * its purpose first).
 */
export async function completeSignIn(
  params: {
    code?: string | undefined;
    state?: string | undefined;
    error?: string | undefined;
  },
  pending: PendingSignIn | null = consumePendingSignIn(),
): Promise<string> {
  if (params.error) throw new SignInError('provider_error', params.error);
  if (!params.code) throw new SignInError('missing_code', 'No authorization code was returned.');
  if (!pending || pending.state !== params.state) {
    throw new SignInError('state_mismatch', 'This sign-in link has expired. Please try again.');
  }
  try {
    await exchangeCode({
      code: params.code,
      code_verifier: pending.code_verifier,
      nonce: pending.nonce,
      redirect_uri: pending.redirect_uri,
    });
    await reloadMe();
  } catch (error) {
    // POST /auth/session surfaces identity failures as SIGN_IN_FAILED (401) or
    // IDENTITY_PROVIDER_UNAVAILABLE (503, retryable). The code is single-use,
    // so either way the person starts sign-in again.
    if (isApiError(error)) {
      if (error.isNetwork) throw new SignInError('offline', error.message);
      // The person asked for their Build data to be erased and it was: Build
      // will not recreate them from the KorOS identity (403 ACCOUNT_ERASED).
      if (error.code === 'ACCOUNT_ERASED') throw new SignInError('account_erased', error.message);
      if (error.code === 'IDENTITY_PROVIDER_UNAVAILABLE' || error.status === 503) {
        throw new SignInError('provider_unavailable', error.message);
      }
      throw new SignInError('exchange_failed', error.message);
    }
    throw error;
  }
  bootPromise = Promise.resolve(snapshot);
  // An action parked for step-up may now be resumed (after confirmation).
  markStepUpSignedIn();
  return pending.return_to;
}

/**
 * Development/E2E only: sign in as a seeded demo user without KorOS
 * (`POST /auth/dev-session`, which the API answers 404 unless
 * DEV_LOGIN_ENABLED). Only reachable from the dev sign-in panel, which is
 * compiled out of normal production builds.
 */
export async function devSignIn(email: string): Promise<void> {
  const response = await api.post<AuthTokenResponse>(
    '/auth/dev-session',
    { email },
    { skipAuthRetry: true },
  );
  setAccessToken(response.data.access_token, response.data.expires_in);
  await reloadMe();
  bootPromise = Promise.resolve(snapshot);
}

/**
 * Sign out: revoke server-side (best effort) and wipe local protected data.
 * The caller is responsible for warning first if the outbox is non-empty.
 *
 * Returns KorOS's sign-out URL when Build's session came from KorOS (single
 * sign-out; the caller sends the browser there), or null for a local sign-out
 * (offline, a dev session, or KorOS not publishing an end-session endpoint).
 */
export async function signOut(): Promise<string | null> {
  let logoutUrl: string | null = null;
  try {
    logoutUrl = (await logoutRequest()).logout_url;
  } catch {
    // Offline or already revoked: local data is still cleared below.
  }
  setAccessToken(null);
  await clearProtectedData();
  bootPromise = Promise.resolve({ status: 'signed_out', me: null });
  set({ status: 'signed_out', me: null });
  return logoutUrl;
}

// ---------------------------------------------------------------------------
// Staff "view as" (read-only, accounts.view_as) — see ./view-as.ts
// ---------------------------------------------------------------------------

type ViewAsExchange = { access_token: string; expires_in: number; view_as: ViewAsInfo };

// A view-as session that ended by itself (time up, refused by the server): the token goes.
subscribeToViewAs(() => {
  const { active, ended } = getViewAs();
  if (!active && ended) setAccessToken(null);
});

/**
 * Open the app as someone, read-only, from the back office's one-time code. The local
 * database becomes memory-only first, so nothing of theirs is stored in this browser,
 * and the staff member's own token (if any) is dropped: this tab is theirs no more.
 */
export async function startViewAs(code: string, backend: MemoryBackend): Promise<ViewAsInfo> {
  switchToMemoryDatabase(backend);
  setAccessToken(null);
  const response = await api.post<ViewAsExchange>(
    '/auth/view-as/exchange',
    { code },
    { skipAuthRetry: true },
  );
  beginViewAs(response.data.view_as);
  setAccessToken(response.data.access_token, response.data.expires_in);
  await reloadMe();
  bootPromise = Promise.resolve(snapshot);
  return response.data.view_as;
}

/** Exit: end the session on the server (best effort) and say where the staff member goes back to. */
export async function exitViewAs(): Promise<string> {
  const fallback = getViewAs().active?.return_url ?? '/backoffice/';
  let target = fallback;
  try {
    const response = await api.post<{ return_url?: string }>(
      '/auth/view-as/end',
      {},
      { skipAuthRetry: true },
    );
    target = response.data?.return_url ?? fallback;
  } catch {
    // Already ended or offline: it ends by itself at its time anyway.
  }
  endViewAsLocally('exited');
  setAccessToken(null);
  bootPromise = Promise.resolve({ status: 'signed_out', me: null });
  set({ status: 'signed_out', me: null });
  // Only ever a back-office path (never somewhere the server did not name).
  return target.startsWith('/backoffice/') ? target : '/backoffice/';
}

/** Send the browser to KorOS to end its session too (`/signed-out` is where it comes back). */
export function leaveForKorosSignOut(url: string): void {
  redirectTo(url);
}

/** Test helper. */
export function resetSessionForTests(next: SessionSnapshot = { status: 'booting', me: null }) {
  bootPromise = null;
  set(next);
}
